import {
  canvasPageIdAtom,
  editorPagesAtom,
  isEditorReadyAtom,
} from '@/atoms/_editorAtoms'
import {
  selectedPageAtom,
  watermarkIdAtom,
  pageLayoutAtom,
} from '@/atoms/editorAtoms'
import {
  itemAtomFamily,
  itemIdAtom,
  watermarkAtomFamily,
  type EditorItem,
} from '@/atoms/_editorAtoms'
import { default as _omit } from 'lodash/omit'
import { useRecoilCallback } from 'recoil'
import { editorPageJsonItem } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { useEditorFlushTextBoxGeometry } from '@/hooks/editor/useEditorFlushTextBoxGeometry'
import { logWipeProvenance } from '@/utils/editor/wipeProvenance'

/**
 * Syncs the current page's items from itemAtomFamily to editorPagesAtom.
 * This is a direct atomic update that avoids selector timing issues.
 *
 * ## Why This Exists
 * This utility performs the same type of sync as `saveItems` in
 * `useUpdatePageItems.js`, without its stale-canvas and wipe guards and
 * without the Lotus geometry flush ordering the save path needs. Both write
 * `editorPagesAtom` with one direct `set()` and hand the written array
 * back; a snapshot taken right after either resolves still reads the tree
 * from before the write (Recoil 0.5, `cloneSnapshot`), so a caller that
 * needs the synced pages uses the return value, never a fresh read.
 *
 * ## When to Use
 * - Before bulk operations that process multiple pages
 * - When the guards of `saveItems` do not apply (a box insert on the page
 *   the canvas shows, a quick action)
 *
 * ## TypeScript Notes
 * Similar to `useUpdatePageItems.js`, this utility uses `any` types and type
 * assertions because the atom types haven't been migrated to TypeScript yet.
 * These will be fixed when we convert atoms to TypeScript in a future PR.
 *
 * @TODO Convert atoms to TypeScript and remove type assertions
 */
export const useSyncCurrentPageToEditorPages = () => {
  const flushEditorTextBoxGeometry = useEditorFlushTextBoxGeometry()
  return useRecoilCallback(
    ({ snapshot, set }) =>
      async () => {
        // Get current state
        const selectedPage = await snapshot.getPromise(selectedPageAtom)
        const itemIds = await snapshot.getPromise(itemIdAtom)
        const editorPages = await snapshot.getPromise(editorPagesAtom)
        const watermarkIds = await snapshot.getPromise(watermarkIdAtom)
        const themeOverride = await snapshot.getPromise(pageLayoutAtom)

        // Collect all items for current page from itemAtomFamily
        const currentPageItems: EditorItem[] = []
        for (const id of itemIds) {
          const item = await snapshot.getPromise(itemAtomFamily(id))
          if (item?.type) {
            currentPageItems.push(
              editorPageJsonItem(JSON.parse(JSON.stringify(item))),
            )
          }
        }

        // Collect watermarks
        const currentPageWatermarks: EditorItem[] = []
        for (const id of watermarkIds) {
          const watermark = await snapshot.getPromise(watermarkAtomFamily(id))
          if (watermark?.type) {
            currentPageWatermarks.push(JSON.parse(JSON.stringify(watermark)))
          }
        }

        // OBSERVABILITY (log-only, the stamp below proceeds unchanged): this
        // sync is a canvas→pages writer with no seatbelts, and the production
        // page-wipe audit signature (a populated page saved with zero items
        // and a freshly-stamped themeOverride) is exactly what it produces
        // when the canvas atoms don't hold the selected page's content. When
        // this stamp is wipe-shaped or the canvas belongs to another page,
        // emit full provenance so the next field occurrence names its writer
        // and atom state — correlate with the backend's suspicious_page_wipe
        // audit row via page id + timestamp.
        const canvasPageId = await snapshot.getPromise(canvasPageIdAtom)
        const isEditorReady = await snapshot.getPromise(isEditorReadyAtom)
        const targetPage = editorPages[selectedPage - 1] as
          | { product?: { orderPageId?: string; items?: unknown[] } }
          | undefined
        const targetPageId = targetPage?.product?.orderPageId
        const currentItems = targetPage?.product?.items ?? []
        const wipeShaped =
          currentPageItems.length === 0 && currentItems.length > 0
        const canvasStale =
          !!canvasPageId && !!targetPageId && canvasPageId !== targetPageId
        if (wipeShaped || canvasStale) {
          logWipeProvenance(
            'editor_bulk_sync_wipe_shaped_stamp',
            'bulk-op sync stamped a wipe-shaped or cross-page payload',
            {
              writer: 'useSyncCurrentPageToEditorPages',
              wipeShaped,
              canvasStale,
              selectedPage,
              canvasPageId,
              targetPageId,
              isEditorReady,
              itemIdAtomLength: itemIds.length,
              collectedItemCount: currentPageItems.length,
              currentItemsLength: currentItems.length,
              pageItemVector: (
                editorPages as { product?: { items?: unknown[] } }[]
              ).map((p) => p.product?.items?.length ?? -1),
            },
          )
        }

        // Prepare theme override (omit certain fields)
        const omitFields = [
          'archived',
          'available',
          'id',
          'layout_id',
          'paper_type',
          'product_id',
        ]
        const cleanedThemeOverride = _omit(themeOverride, omitFields)

        // Update editorPagesAtom directly - this is atomic
        const updatedPages = editorPages.map((page, index) => {
          if (index === selectedPage - 1) {
            return {
              ...page,
              product: {
                ...page.product,
                items: currentPageItems,
                watermarks: currentPageWatermarks,
                themeOverride: cleanedThemeOverride,
              },
            }
          }
          return page
        })

        // Atomic update - no selector delays
        // @ts-expect-error - @TODO: Fix type incompatibility when atoms are migrated to TypeScript.
        // This follows the same pattern as useUpdatePageItems.js but with direct set() instead
        // of objectSelector. The type mismatch occurs because editorPagesAtom isn't typed yet.
        set(editorPagesAtom, updatedPages)

        await flushEditorTextBoxGeometry()

        // Return the updated pages for verification if needed
        return updatedPages
      },
    [],
  )
}
